import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useRef, useEffect, useCallback, useState } from 'react'

import { DOT_RADIUS } from '@aytm/app/stats/page/question-views/hm/components/chart/lib/constants'

import style from '@aytm/app/stats/page/question-views/hm/components/chart/components/comments-popup/styles/CommentsPopup.module.scss'

const ARROW_WIDTH = 12
const ALLOWED_GAP = 20

const CommentsPopup = ({ comments, coords, image, scale, onMouseEnter, onMouseLeave, onCommentsClick }) => {
  const popupRef = useRef(null)
  const [position, setPosition] = useState({ left: 0, top: 0 })
  const [arrowTop, setArrowTop] = useState(0)
  const [placement, setPlacement] = useState('arrowLeft')
  const [isVisible, setIsVisible] = useState(false)

  const imageWidth = image?.original?.width || 0
  const imageHeight = image?.original?.height || 0

  const calcPosition = useCallback(() => {
    if (!popupRef.current || !coords) return

    const popupHeight = popupRef.current.getBoundingClientRect().height || 100
    const popupWidth = popupRef.current.getBoundingClientRect().width || 215

    const [cx, cy] = coords
    let left, top, arrowPosTop, placementClass

    if (cx < imageWidth / 2) {
      left = cx * scale + ARROW_WIDTH + DOT_RADIUS
      placementClass = 'arrowLeft'
    } else {
      left = cx * scale - ARROW_WIDTH - popupWidth - DOT_RADIUS
      placementClass = 'arrowRight'
    }

    if (cy <= imageHeight / 3) {
      if (popupHeight / 2 - ALLOWED_GAP <= cy * scale) {
        top = cy * scale - popupHeight / 2
        arrowPosTop = popupHeight / 2
      } else {
        const marginTop = cy * scale + ALLOWED_GAP
        top = cy * scale - marginTop
        arrowPosTop = marginTop
      }
    } else if (cy >= (imageHeight * 2) / 3) {
      if (popupHeight / 2 <= (imageHeight - cy) * scale + ALLOWED_GAP) {
        top = cy * scale - popupHeight / 2
        arrowPosTop = popupHeight / 2
      } else {
        const marginTop = (imageHeight - cy) * scale + ALLOWED_GAP
        top = cy * scale - popupHeight + marginTop
        arrowPosTop = popupHeight - marginTop
      }
    } else {
      top = cy * scale - popupHeight / 2
      arrowPosTop = popupHeight / 2
    }

    setPosition({ left, top })
    setArrowTop(arrowPosTop)
    setPlacement(placementClass)
  }, [coords, imageWidth, imageHeight, scale])

  useEffect(() => {
    setIsVisible(false)
    calcPosition()

    const timer = setTimeout(() => {
      calcPosition()
      setIsVisible(true)
    }, 50)

    return () => clearTimeout(timer)
  }, [calcPosition])

  if (!comments?.length) return null

  return (
    <div
      ref={popupRef}
      className={classNames(style.popup, style[placement], { [style.visible]: isVisible })}
      style={{ left: position.left, top: position.top }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <span className={style.arrow} style={{ top: arrowTop }} />
      <div className={style.content}>
        {comments.map((comment, i) => (
          <div key={comment.uid || i} className={style.item}>
            &ldquo;{comment.text}&rdquo;
            <a
              id={`comments_${comment.path.join('_')}`}
              data-response-id={comment.uid}
              className={style.link}
              aria-label="Show this comment in Comments"
              href="#comments"
              onClick={event => onCommentsClick?.(event, { responseId: comment.uid, batchPosition: comment.path[1] })}
            >
              <i className="icon-aytmfd-comments-v2" />
            </a>
          </div>
        ))}
      </div>
    </div>
  )
}

CommentsPopup.displayName = 'CommentsPopup'

CommentsPopup.propTypes = {
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      path: PropTypes.array,
      uid: PropTypes.number
    })
  ),
  coords: PropTypes.arrayOf(PropTypes.number),
  image: PropTypes.object,
  scale: PropTypes.number,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onCommentsClick: PropTypes.func
}

export default CommentsPopup
